import { Component, type CSSProperties, type JSX, type ReactNode, useMemo, useRef } from 'react';

import { useElementWidth, usePrefersReducedMotion } from './motion';

/**
 * The "Pharmacy Delivery" screen inside the Pharmacy OS showcase — animated.
 *
 * Ported from the design's `OS Pharmacy Delivery Screen.dc.html`: a route
 * optimisation screen with three zone routes, numbered stops, a scooter and a
 * bike riding their routes, and the driver app beside it.
 *
 * ## It is a picture of the product, not the product
 *
 * Every pharmacy, patient, address and figure here is the design's invented
 * demonstration data, written into this file. Nothing is read from the
 * platform, so a marketing page can never show a real customer, patient,
 * driver or delivery. The map is drawn from rectangles — no tiles, no map
 * provider, no network request and no licence to attribute.
 *
 * ## It degrades to the shipped screenshot
 *
 * Under reduced motion, before the section has come near the viewport, and if
 * anything here throws, the approved static snapshot of the same screen is
 * drawn instead. The homepage never depends on this animation working.
 */

const BASE_WIDTH = 1600;
const BASE_HEIGHT = 840;

export function PharmacyDeliveryPreview({
  fallbackSrc,
  alt,
  live,
}: {
  readonly fallbackSrc: string;
  readonly alt: string;
  /** False until the section is near the viewport, so the page loads light. */
  readonly live: boolean;
}): JSX.Element {
  const reduced = usePrefersReducedMotion();
  const frameRef = useRef<HTMLDivElement | null>(null);
  const width = useElementWidth(frameRef);
  const scale = width > 0 ? width / BASE_WIDTH : 0;

  // The frame is always rendered, so it can be measured before the animation
  // is allowed to start; the snapshot fills it until then.
  const snapshot = <img className="jt-os__shot" src={fallbackSrc} alt="" loading="lazy" decoding="async" />;
  const animate = !reduced && live && scale > 0;

  return (
    <div ref={frameRef} className="jt-delivery" role="img" aria-label={alt}>
      {animate ? (
        <PreviewBoundary fallback={snapshot}>
          <div
            className="jt-delivery__canvas"
            style={{ transform: `scale(${scale})`, width: BASE_WIDTH, height: BASE_HEIGHT }}
            aria-hidden="true"
          >
            <DeliveryScreen />
          </div>
        </PreviewBoundary>
      ) : (
        snapshot
      )}
    </div>
  );
}

class PreviewBoundary extends Component<
  { readonly children: ReactNode; readonly fallback: ReactNode },
  { readonly failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

// ---------------------------------------------------------------------------
// The demonstration data, exactly as the design generates it.
// ---------------------------------------------------------------------------

const ZONE_A = 'oklch(0.55 0.2 295)';
const ZONE_B = 'oklch(0.6 0.14 165)';
const ZONE_C = 'oklch(0.68 0.15 65)';
const HOME: readonly [number, number] = [505, 235];

type Point = readonly [number, number];

const ZONE_STOPS: readonly (readonly [string, readonly Point[]])[] = [
  [
    ZONE_A,
    [
      [320, 170],
      [200, 90],
      [80, 170],
      [200, 235],
    ],
  ],
  [
    ZONE_B,
    [
      [505, 320],
      [320, 400],
      [640, 400],
      [780, 320],
    ],
  ],
  [
    ZONE_C,
    [
      [640, 170],
      [780, 90],
      [920, 170],
      [780, 235],
    ],
  ],
];

const XS = [80, 200, 320, 505, 640, 780, 920];
const YS = [90, 170, 235, 320, 400];
const px = (v: number): string => `${((v / 1020) * 100).toFixed(2)}%`;
const py = (v: number): string => `${((v / 470) * 100).toFixed(2)}%`;

/** Out from the pharmacy, along the streets to each stop, and home. */
function manhattan(stops: readonly Point[]): string {
  const all = [HOME, ...stops, HOME];
  let path = `M${HOME[0]} ${HOME[1]}`;
  for (let i = 1; i < all.length; i++) {
    const here = all[i] as Point;
    const prev = all[i - 1] as Point;
    path += ` L${here[0]} ${prev[1]} L${here[0]} ${here[1]}`;
  }
  return path;
}

interface Rect {
  readonly x: string;
  readonly y: string;
  readonly w: string;
  readonly h: string;
  readonly c: string;
  readonly r?: string;
}

function buildMap(): { blocks: Rect[]; buildings: Rect[]; trees: { x: string; y: string; s: string }[] } {
  const bx = [-60, ...XS, 1080];
  const by = [-60, ...YS, 530];
  const parks = new Set(['1,2', '5,3', '6,1']);
  const blocks: Rect[] = [];
  const buildings: Rect[] = [];
  const trees: { x: string; y: string; s: string }[] = [];
  // The design's seeded generator, so the map is the same on every load.
  let seed = 7;
  const rnd = (): number => (seed = (seed * 9301 + 49297) % 233280) / 233280;
  const shades = ['#d3d9df', '#d9dee3', '#ccd3da', '#dfe3e7'];

  for (let i = 0; i < bx.length - 1; i++) {
    for (let j = 0; j < by.length - 1; j++) {
      const x0 = (bx[i] as number) + 10;
      const x1 = (bx[i + 1] as number) - 10;
      const y0 = (by[j] as number) + 9;
      const y1 = (by[j + 1] as number) - 9;

      if (parks.has(`${i},${j}`)) {
        blocks.push({ x: px(x0), y: py(y0), w: px(x1 - x0), h: py(y1 - y0), c: '#d4e6d6', r: '10px' });
        for (let t = 0; t < 14; t++) {
          trees.push({
            x: px(x0 + 8 + rnd() * (x1 - x0 - 16)),
            y: py(y0 + 6 + rnd() * (y1 - y0 - 12)),
            s: `${5 + Math.round(rnd() * 4)}px`,
          });
        }
        continue;
      }

      blocks.push({ x: px(x0), y: py(y0), w: px(x1 - x0), h: py(y1 - y0), c: '#e7eaed', r: '3px' });
      const rows = y1 - y0 > 60 ? 2 : 1;
      for (let r = 0; r < rows; r++) {
        const ry0 = y0 + 3 + (r * (y1 - y0)) / rows;
        const ry1 = y0 - 3 + ((r + 1) * (y1 - y0)) / rows;
        let cx = x0 + 3;
        while (cx < x1 - 14) {
          const w = Math.min(x1 - 3 - cx, 14 + rnd() * 34);
          const inset = rnd() < 0.3 ? 2 + rnd() * 5 : 0;
          buildings.push({
            x: px(cx),
            y: py(ry0 + inset),
            w: px(w - 3),
            h: py(ry1 - ry0 - inset * (rnd() < 0.5 ? 1 : 2)),
            c: shades[Math.floor(rnd() * shades.length)] as string,
          });
          cx += w;
        }
      }
    }
  }
  return { blocks, buildings, trees };
}

const ROADS = [
  ...XS.map((x, i) => ({
    left: `calc(${px(x)} - ${i === 3 ? 8 : 6}px)`,
    top: '0',
    width: `${i === 3 ? 16 : 12}px`,
    height: '100%',
  })),
  ...YS.map((y, i) => ({
    left: '0',
    top: `calc(${py(y)} - ${i === 2 ? 8 : 6}px)`,
    width: '100%',
    height: `${i === 2 ? 16 : 12}px`,
  })),
];

const LABELS: readonly (readonly [string, number, number])[] = [
  ['OLDHAM ROAD', 380, 74],
  ['GREAT ANCOATS ST', 680, 154],
  ['PICCADILLY', 130, 219],
  ['PORTLAND ST', 560, 304],
  ['WHITWORTH ST', 80, 384],
  ['RIVER MEDLOCK', 700, 452],
  ['ROCHDALE RD', 860, 300],
];

const POIS = [
  { x: 140, y: 196, t: 'Piccadilly Gdns', i: '•', bg: '#3f9e6f' },
  { x: 710, y: 272, t: 'Sackville Park', i: '•', bg: '#3f9e6f' },
  { x: 412, y: 130, t: 'GP surgery', i: '+', bg: '#0d7d9c' },
  { x: 850, y: 124, t: 'Cutting Room Sq', i: '•', bg: '#3f9e6f' },
  { x: 262, y: 124, t: 'Car park', i: 'P', bg: '#4a6fb5' },
  { x: 570, y: 390, t: 'Tesco Express', i: '•', bg: '#8a7fb5' },
];

const ROUTES = ZONE_STOPS.map(([colour, stops], k) => ({
  colour,
  d: manhattan(stops),
  dur: `${[24, 30, 26][k] as number}s`,
  bike: k === 1,
}));

const STOPS = (() => {
  const count = new Map<string, number>();
  return ZONE_STOPS.flatMap(([colour, stops]) =>
    stops.map(([x, y]) => {
      const n = (count.get(colour) ?? 0) + 1;
      count.set(colour, n);
      return { x: `${((x / 1020) * 100).toFixed(1)}%`, y: `${((y / 470) * 100).toFixed(1)}%`, colour, n };
    }),
  );
})();

const TOP_NAV: readonly (readonly [string, string?])[] = [
  ['Home'],
  ['Marketplace'],
  ['Customers & People'],
  ['Pharmacy OS', '23'],
  ['Clinical'],
  ['Inventory'],
  ['Fulfilment & Logistics'],
];
const DELIVERY_NAV: readonly (readonly [string, string, boolean?])[] = [
  ['Route Optimisation', '', true],
  ['Add Patients', ''],
  ['Area Splitting', ''],
  ['Care Home Drops', '4'],
  ['Dossette Scheduling', '12'],
  ['Failed Deliveries', '3'],
  ['Alternative Addresses', ''],
  ['Drivers & Vans', ''],
];
const BOTTOM_NAV = ['Payments & Finance', 'Procurement', 'Partners', 'Workforce', 'PENNY Intelligence'];
const ADD_WAYS = [
  { ic: '⌁', t: 'Scan bag labels', s: 'AI-powered OCR', on: true },
  { ic: '⇪', t: 'Import Excel / CSV', s: 'Bulk upload', on: false },
  { ic: '+', t: 'Enter manually', s: 'Single patient', on: false },
];
const ZONES = [
  { n: 'Zone A · 22', c: ZONE_A },
  { n: 'Zone B · 19', c: ZONE_B },
  { n: 'Zone C · 23', c: ZONE_C },
];
const STATS = [
  { k: 'Mileage saved', v: '−31%', c: '#127a4f' },
  { k: 'Total distance', v: '48.6 mi', c: '#17112b' },
  { k: 'Est. finish', v: '15:40', c: '#17112b' },
  { k: 'On time', v: '98%', c: '#5b2ede' },
];
const FEATURES = [
  'Area-wise delivery splitting',
  'Bulk scan deliveries',
  'Care home bulk drops',
  'Dossette box scheduling',
  'Bulk reschedule',
  'Reschedule failed deliveries',
  'Alternative addresses',
  'Delivery notes',
];
const MINI_BLOCKS = [
  [0, 0, 16, 22],
  [56, 0, 40, 22],
  [116, 0, 72, 14],
  [0, 32, 16, 24],
  [34, 32, 62, 24],
  [116, 32, 72, 24],
  [212, 0, 24, 56],
  [0, 74, 16, 46],
  [34, 74, 62, 46],
  [116, 74, 48, 46],
  [176, 74, 60, 46],
] as const;

// ---------------------------------------------------------------------------
// The screen, at its native 1600 × 840.
// ---------------------------------------------------------------------------

const abs = (style: CSSProperties): CSSProperties => ({ position: 'absolute', ...style });

function DeliveryScreen(): JSX.Element {
  const map = useMemo(buildMap, []);

  return (
    <div className="jt-dl">
      <aside className="jt-dl__side">
        <div className="jt-dl__brand">Just Treated</div>
        {TOP_NAV.map(([name, badge]) => (
          <div key={name} className="jt-dl__nav">
            <span className="jt-dl__navname">
              {name}
              {badge !== undefined ? <span className="jt-dl__badge">{badge}</span> : null}
            </span>
            <span className="jt-dl__caret">▼</span>
          </div>
        ))}
        <div className="jt-dl__nav jt-dl__nav--open">
          <span>Pharmacy Delivery</span>
          <span className="jt-dl__caret">▲</span>
        </div>
        {DELIVERY_NAV.map(([name, count, on]) => (
          <div key={name} className={`jt-dl__sub ${on === true ? 'jt-dl__sub--on' : ''}`}>
            <span>{name}</span>
            {count !== '' ? <span className="jt-dl__count">{count}</span> : null}
          </div>
        ))}
        {BOTTOM_NAV.map((name) => (
          <div key={name} className="jt-dl__nav">
            <span>{name}</span>
            <span className="jt-dl__caret">▼</span>
          </div>
        ))}
      </aside>

      <div className="jt-dl__main">
        <header className="jt-dl__top">
          <div className="jt-dl__branch">
            <span className="jt-dl__op">OP</span>
            <div className="jt-dl__col">
              <span className="jt-dl__branchname">Online Pharmacy 4U</span>
              <span className="jt-dl__muted">Manchester Central · Branch 3 of 6</span>
            </div>
            <span className="jt-dl__caret jt-dl__caret--muted">▼</span>
          </div>
          <div className="jt-dl__col jt-dl__crumbs">
            <span className="jt-dl__muted jt-dl__crumb">Pharmacy Delivery / Route Optimisation</span>
            <span className="jt-dl__title">Pharmacy Delivery</span>
          </div>
          <div className="jt-dl__grow" />
          <div className="jt-dl__search">
            <span>Search anything...</span>
            <span className="jt-dl__kbd">⌘K</span>
          </div>
          <div className="jt-dl__icons">
            {['●', '■', '✓', '?'].map((icon) => (
              <span key={icon} className="jt-dl__icon">
                {icon}
              </span>
            ))}
          </div>
          <div className="jt-dl__viewing">
            Viewing as <b>Platform admin</b>
          </div>
        </header>

        <main className="jt-dl__body">
          <section className="jt-dl__hero">
            <div className="jt-dl__col jt-dl__grow">
              <span className="jt-dl__h1">Smart Route Optimisation</span>
              <span className="jt-dl__sub2">
                AI route planning that minimises mileage and maximises efficiency
              </span>
            </div>
            <span className="jt-dl__eyebrow">ADD PATIENTS</span>
            <div className="jt-dl__ways">
              {ADD_WAYS.map((way) => (
                <div key={way.t} className={`jt-dl__way ${way.on ? 'jt-dl__way--on' : ''}`}>
                  <span className="jt-dl__wayicon">{way.ic}</span>
                  <div className="jt-dl__col">
                    <span className="jt-dl__waytitle">{way.t}</span>
                    <span className="jt-dl__waysub">{way.s}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div className="jt-dl__grid">
            <section className="jt-dl__mapcard">
              <div className="jt-dl__maphead">
                <span className="jt-dl__maptitle">Today&rsquo;s routes</span>
                <span className="jt-dl__muted jt-dl__small">Tue 6 Oct · 64 deliveries · 3 drivers</span>
                <div className="jt-dl__grow" />
                {ZONES.map((zone) => (
                  <span key={zone.n} className="jt-dl__zone">
                    <span className="jt-dl__dot" style={{ background: zone.c }} />
                    {zone.n}
                  </span>
                ))}
                <span className="jt-dl__reopt">Re-optimise</span>
              </div>

              <div className="jt-dl__map">
                {map.blocks.map((b, i) => (
                  <span
                    key={`b${i}`}
                    style={abs({
                      left: b.x,
                      top: b.y,
                      width: b.w,
                      height: b.h,
                      background: b.c,
                      borderRadius: b.r,
                    })}
                  />
                ))}
                {map.buildings.map((b, i) => (
                  <span
                    key={`h${i}`}
                    className="jt-dl__building"
                    style={abs({ left: b.x, top: b.y, width: b.w, height: b.h, background: b.c })}
                  />
                ))}
                {map.trees.map((t, i) => (
                  <span
                    key={`t${i}`}
                    className="jt-dl__tree"
                    style={abs({ left: t.x, top: t.y, width: t.s, height: t.s })}
                  />
                ))}
                <span className="jt-dl__river" />
                {ROADS.map((road, i) => (
                  <span key={`r${i}`} style={abs({ ...road, background: '#ffffff' })} />
                ))}
                {LABELS.map(([t, x, y]) => (
                  <span key={t} className="jt-dl__label" style={abs({ left: px(x), top: py(y) })}>
                    {t}
                  </span>
                ))}
                {POIS.map((poi) => (
                  <span key={poi.t} className="jt-dl__poi" style={abs({ left: px(poi.x), top: py(poi.y) })}>
                    <span className="jt-dl__poiicon" style={{ background: poi.bg }}>
                      {poi.i}
                    </span>
                    <span className="jt-dl__poiname">{poi.t}</span>
                  </span>
                ))}

                <svg className="jt-dl__routes" viewBox="0 0 1020 470" preserveAspectRatio="none" fill="none">
                  {ROUTES.map((route) => (
                    <g key={route.d}>
                      <path
                        d={route.d}
                        stroke="rgba(23,17,43,.08)"
                        strokeWidth="14"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d={route.d}
                        stroke="#ffffff"
                        strokeWidth="9"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d={route.d}
                        stroke={route.colour}
                        strokeWidth="5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <g>
                        <animateMotion
                          dur={route.dur}
                          repeatCount="indefinite"
                          rotate="auto"
                          path={route.d}
                        />
                        <circle r="22" fill={route.colour} fillOpacity=".16" />
                        {route.bike ? <Bike colour={route.colour} /> : <Scooter colour={route.colour} />}
                      </g>
                    </g>
                  ))}
                </svg>

                {STOPS.map((stop) => (
                  <span
                    key={`${stop.colour}${stop.n}`}
                    className="jt-dl__stop"
                    style={abs({ left: stop.x, top: stop.y, background: stop.colour })}
                  >
                    {stop.n}
                  </span>
                ))}

                <span className="jt-dl__home">
                  <span className="jt-dl__rx">Rx</span>
                  <span className="jt-dl__homename">
                    Online
                    <br />
                    Pharmacy 4U
                  </span>
                </span>

                <div className="jt-dl__stats">
                  {STATS.map((stat) => (
                    <div key={stat.k} className="jt-dl__stat">
                      <span className="jt-dl__statkey">{stat.k}</span>
                      <span className="jt-dl__statval" style={{ color: stat.c }}>
                        {stat.v}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="jt-dl__ocr">
                  <span className="jt-dl__ocrtitle">Scan bag labels · AI OCR</span>
                  <div className="jt-dl__ocrrow">
                    <span className="jt-dl__barcode" />
                    <div className="jt-dl__col jt-dl__grow">
                      <span className="jt-dl__ocrname">J. Patel · M8 0QL</span>
                      <span className="jt-dl__ocrok">Read · added to Zone B</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="jt-dl__features">
                {FEATURES.map((feature) => (
                  <span key={feature} className="jt-dl__feature">
                    {feature}
                  </span>
                ))}
              </div>
            </section>

            <section className="jt-dl__phonecard">
              <div className="jt-dl__phone">
                <div className="jt-dl__status">
                  <span>9:41</span>
                  <span>●●●</span>
                </div>
                <div className="jt-dl__col jt-dl__driverhead">
                  <span className="jt-dl__drivermuted">Driver app · Zone A</span>
                  <span className="jt-dl__driverstop">Stop 4 of 22</span>
                </div>
                <div className="jt-dl__minimap">
                  {MINI_BLOCKS.map(([x, y, w, h], k) => (
                    <span
                      key={`${x}-${y}`}
                      style={abs({
                        left: x,
                        top: y,
                        width: w,
                        height: h,
                        background: k === 4 ? '#dbe8dc' : '#e2e6e9',
                        borderRadius: 3,
                      })}
                    />
                  ))}
                  <svg className="jt-dl__minisvg" viewBox="0 0 236 120" fill="none">
                    <path
                      d="M24 104 L24 64 L104 64 L104 24 L200 24"
                      stroke="#b9c2ca"
                      strokeWidth="5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M24 104 L24 64 L104 64 L104 40"
                      stroke="#fff"
                      strokeWidth="9"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M24 104 L24 64 L104 64 L104 40"
                      stroke="#17112b"
                      strokeWidth="5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <g>
                      <animateMotion
                        dur="7s"
                        repeatCount="indefinite"
                        rotate="auto"
                        path="M104 40 L104 24 L200 24"
                      />
                      <g transform="scale(0.75)">
                        <circle r="20" fill="rgba(91,46,222,.18)" />
                        <Scooter colour="oklch(0.55 0.2 295)" />
                      </g>
                    </g>
                  </svg>
                  <span className="jt-dl__ministop" />
                  <span className="jt-dl__ministoplabel">STOP 4</span>
                </div>
                <div className="jt-dl__driverbody">
                  <div className="jt-dl__col">
                    <span className="jt-dl__patient">Margaret Clarke</span>
                    <span className="jt-dl__address">14 Pollard St, Manchester M4 5BQ</span>
                  </div>
                  <div className="jt-dl__chips">
                    <span className="jt-dl__chip jt-dl__chip--amber">Dossette box</span>
                    <span className="jt-dl__chip jt-dl__chip--violet">Signature</span>
                  </div>
                  <div className="jt-dl__note">
                    <b>Note:</b> Ring twice. If no answer, leave with neighbour at no. 16.
                  </div>
                  <div className="jt-dl__actions">
                    <span className="jt-dl__primary">Delivered · capture signature</span>
                    <div className="jt-dl__pair">
                      <span className="jt-dl__secondary">Alt. address</span>
                      <span className="jt-dl__secondary jt-dl__secondary--bad">Failed</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}

function Scooter({ colour }: { readonly colour: string }): JSX.Element {
  return (
    <g>
      <ellipse cx="0" cy="2" rx="17" ry="9" fill="rgba(23,17,43,.18)" />
      <rect x="10" y="-2.2" width="7" height="4.4" rx="2" fill="#2c2f38" />
      <rect x="-17" y="-2.4" width="7" height="4.8" rx="2" fill="#2c2f38" />
      <rect x="-12" y="-5" width="25" height="10" rx="5" fill={colour} stroke="#fff" strokeWidth="1" />
      <rect x="8" y="-8.5" width="2.6" height="17" rx="1.3" fill="#2c2f38" />
      <rect x="-19" y="-7.5" width="11" height="15" rx="2.2" fill="#5b2ede" stroke="#fff" strokeWidth="1.2" />
      <rect x="-16" y="-1" width="5" height="2" rx="1" fill="#fff" />
      <ellipse cx="-1" cy="0" rx="5.5" ry="8" fill="#17112b" />
      <circle cx="2.5" cy="0" r="4.6" fill="#fff" stroke="#17112b" strokeWidth="1.4" />
      <rect x="4.6" y="-2.2" width="1.6" height="4.4" rx=".8" fill="#2c2f38" />
    </g>
  );
}

function Bike({ colour }: { readonly colour: string }): JSX.Element {
  return (
    <g>
      <ellipse cx="0" cy="2" rx="17" ry="7" fill="rgba(23,17,43,.16)" />
      <rect x="8" y="-1.6" width="10" height="3.2" rx="1.6" fill="#2c2f38" />
      <rect x="-18" y="-1.6" width="10" height="3.2" rx="1.6" fill="#2c2f38" />
      <rect x="-12" y="-1" width="24" height="2" rx="1" fill="#8a93a0" />
      <rect x="8.5" y="-7" width="2.2" height="14" rx="1.1" fill="#2c2f38" />
      <rect x="-9" y="-5.5" width="7.5" height="11" rx="1.8" fill="#5b2ede" stroke="#fff" strokeWidth="1.1" />
      <ellipse cx="0.5" cy="0" rx="4.8" ry="7.5" fill={colour} stroke="#fff" strokeWidth="1" />
      <circle cx="3" cy="0" r="4" fill="#fff" stroke="#17112b" strokeWidth="1.4" />
    </g>
  );
}
